/**
 * Installer Portal Executions — thin client wrappers (Sprint 0009).
 *
 * Read surface (RLS-gated direct queries):
 *   - listExecutionsForOrg(orgId)
 *   - listExecutionsForCrew()
 *   - getExecutionById(id)
 *   - listExecutionEvents(executionId)
 *   - listExecutionProofs(executionId)
 *
 * Mutation surface (Edge Functions only):
 *   - acknowledgeExecution
 *   - updateExecutionStatus (start | pause_for_proof | resume |
 *                            submit_for_review | flag_callback | note)
 *   - requestExecutionProofUploadUrl
 *   - requestExecutionProofReadUrl
 *
 * App remains canonical for job execution. These wrappers operate only on
 * the portal projection (installer_opportunities). Local fallback:
 * mutations return { ok:false, error:'not_configured' } and reads return [].
 */
import { supabase } from "@/lib/supabase";

const isSupabaseConfigured = Boolean(
  import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY,
);

export type ExecutionStatus =
  | "assigned" | "acknowledged" | "in_progress"
  | "awaiting_proof" | "submitted_for_review"
  | "completed" | "callback_flagged";

export type ExecutionProofKind =
  | "photo" | "document" | "checklist" | "signature" | "other";

export type ExecutionEventKind =
  | "acknowledged" | "progress_update" | "paused_for_proof" | "resumed"
  | "submitted_for_review" | "completion_confirmed" | "callback_flagged" | "note";

export type ExecutionAction =
  | "start" | "pause_for_proof" | "resume"
  | "submit_for_review" | "flag_callback" | "note";

export type ExecutionError =
  | "not_configured"
  | "forbidden"
  | "not_found"
  | "invalid_input"
  | "conflict"
  | "unknown";

type Ok<T> = { ok: true } & T;
type Err = { ok: false; error: ExecutionError; detail?: string };

function normalizeExecutionStatus(status: unknown): ExecutionStatus {
  switch (String(status ?? "").toLowerCase()) {
    case "in_progress":
    case "active":
      return "in_progress";
    case "on_hold":
    case "paused":
    case "awaiting_proof":
      return "awaiting_proof";
    case "completed":
    case "done":
      return "completed";
    case "submitted":
    case "submitted_for_review":
      return "submitted_for_review";
    case "callback":
    case "callback_flagged":
      return "callback_flagged";
    case "acknowledged":
      return "acknowledged";
    case "assigned":
    case "scheduled":
    case "pending":
    case "draft":
    default:
      return "assigned";
  }
}

function classify(err: { context?: { status?: number }; message: string }): Err {
  const status = err.context?.status;
  if (status === 403) return { ok: false, error: "forbidden" };
  if (status === 404) return { ok: false, error: "not_found" };
  if (status === 400) return { ok: false, error: "invalid_input", detail: err.message };
  if (status === 409) return { ok: false, error: "conflict", detail: err.message };
  return { ok: false, error: "unknown", detail: err.message };
}

// ── Types ──────────────────────────────────────────────────
export interface PortalExecution {
  id: string;
  opportunity_id: string;
  organization_id: string;
  app_job_id: string;
  app_offer_id: string | null;
  status: ExecutionStatus;
  schedule_notes: string | null;
  progress_summary: string | null;
  completion_summary: string | null;
  callback_reason: string | null;
  assigned_at: string;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  started_at: string | null;
  paused_at: string | null;
  submitted_at: string | null;
  submitted_by: string | null;
  completed_at: string | null;
  flagged_at: string | null;
  flagged_by: string | null;
}

export interface ExecutionEvent {
  id: string;
  execution_id: string;
  organization_id: string;
  kind: ExecutionEventKind;
  actor_user_id: string | null;
  from_status: ExecutionStatus | null;
  to_status: ExecutionStatus | null;
  note: string | null;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface ExecutionProof {
  id: string;
  execution_id: string;
  organization_id: string;
  uploaded_by: string;
  kind: ExecutionProofKind;
  file_name: string | null;
  mime_type: string | null;
  byte_size: number | null;
  caption: string | null;
  captured_at: string | null;
  uploaded_at: string;
  superseded_by: string | null;
  storage_path?: string | null;
}

const EVENT_COLUMNS =
  "id, execution_id, organization_id, kind, actor_user_id, " +
  "from_status, to_status, note, payload, created_at";

// NOTE: storage_path is read so Koredor App admin/reviewer surfaces can
// mint org/RLS-scoped signed URLs directly. The contractor portal still uses
// the edge function guard for crew-scoped access.
const PROOF_COLUMNS =
  "id, execution_id, organization_id, uploaded_by, kind, file_name, " +
  "mime_type, byte_size, caption, captured_at, uploaded_at, superseded_by, storage_path";

type PortalOpportunityExecutionRow = {
  id: string;
  organization_id: string;
  app_job_id: string | null;
  app_offer_id: string | null;
  status: string | null;
  notes: string | null;
  scope_summary: string | null;
  offered_at: string | null;
  decided_at: string | null;
  scheduled_date: string | null;
  updated_at: string | null;
  created_at: string | null;
};

const OPPORTUNITY_EXECUTION_COLUMNS =
  "id, organization_id, app_job_id, app_offer_id, status, notes, " +
  "scope_summary, offered_at, decided_at, scheduled_date, updated_at, created_at";

function mapOpportunityToPortalExecution(row: PortalOpportunityExecutionRow): PortalExecution {
  const assignedAt = row.offered_at ?? row.scheduled_date ?? row.created_at ?? row.updated_at ?? new Date(0).toISOString();
  const acknowledgedAt = row.status === "accepted" ? row.decided_at : null;
  return {
    id: row.id,
    opportunity_id: row.id,
    organization_id: row.organization_id,
    app_job_id: row.app_job_id ?? "",
    app_offer_id: row.app_offer_id,
    status: normalizeExecutionStatus(row.status),
    schedule_notes: row.notes,
    progress_summary: row.scope_summary,
    completion_summary: null,
    callback_reason: null,
    assigned_at: assignedAt,
    acknowledged_at: acknowledgedAt,
    acknowledged_by: null,
    started_at: null,
    paused_at: null,
    submitted_at: null,
    submitted_by: null,
    completed_at: row.status === "completed" ? row.updated_at : null,
    flagged_at: null,
    flagged_by: null,
  };
}

// ── Reads ──────────────────────────────────────────────────
export async function listExecutionsForOrg(
  organizationId: string,
): Promise<PortalExecution[]> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from("installer_opportunities")
    .select(OPPORTUNITY_EXECUTION_COLUMNS)
    .eq("organization_id", organizationId)
    .order("offered_at", { ascending: false });
  if (error || !data) return [];
  return (data as unknown as PortalOpportunityExecutionRow[]).map(mapOpportunityToPortalExecution);
}

export async function listExecutionsForCrew(): Promise<PortalExecution[]> {
  if (!isSupabaseConfigured) return [];
  // RLS scopes this to contractor crew access via contractor_crews.
  const { data, error } = await supabase
    .from("installer_opportunities")
    .select(OPPORTUNITY_EXECUTION_COLUMNS)
    .order("offered_at", { ascending: false });
  if (error || !data) return [];
  return (data as unknown as PortalOpportunityExecutionRow[]).map(mapOpportunityToPortalExecution);
}

/**
 * Resolve every portal execution for a given APP job id.
 *
 * Read-only convenience for surfaces that want to view website-uploaded
 * proofs from an APP-side jobId (QA review, Job Detail, remediation). RLS
 * scopes the result to executions the caller can already see. Returns []
 * when Supabase isn't configured or the caller has no visibility. This is
 * NOT a reconciliation step — no writes, no mutation, no merging of
 * proofs into the prototype JobSpecExecution.photos[] model.
 */
export async function listExecutionsByAppJobId(
  appJobId: string,
): Promise<PortalExecution[]> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from("installer_opportunities")
    .select(OPPORTUNITY_EXECUTION_COLUMNS)
    .eq("app_job_id", appJobId)
    .order("offered_at", { ascending: false });
  if (error || !data) return [];
  return (data as unknown as PortalOpportunityExecutionRow[]).map(mapOpportunityToPortalExecution);
}

/**
 * Resolve every execution/proof lookup id that may be associated with an APP
 * job id across the historical execution models used by Koredor App and the
 * installer portal.
 *
 * Compatibility sources:
 *   1. `installer_opportunities.id` (portal execution shell)
 *   2. `job_spec_executions.id` (legacy/canonical phase execution rows)
 *   3. the app job id itself (historical proof rows wrote `jobs.id` directly)
 */
export async function listExecutionProofLookupIdsByAppJobId(
  appJobId: string,
): Promise<string[]> {
  if (!isSupabaseConfigured) return [];

  const ids = new Set<string>();
  const normalizedAppJobId = String(appJobId ?? "").trim();
  if (!normalizedAppJobId) return [];
  ids.add(normalizedAppJobId);

  const [portalExecutionsRes, jobSpecExecutionsRes] = await Promise.all([
    supabase
      .from("installer_opportunities")
      .select("id")
      .eq("app_job_id", normalizedAppJobId),
    supabase
      .from("job_spec_executions")
      .select("id")
      .eq("job_id", normalizedAppJobId),
  ]);

  for (const row of (portalExecutionsRes.data ?? []) as Array<{ id?: string | null }>) {
    const id = String(row.id ?? "").trim();
    if (id) ids.add(id);
  }

  for (const row of (jobSpecExecutionsRes.data ?? []) as Array<{ id?: string | null }>) {
    const id = String(row.id ?? "").trim();
    if (id) ids.add(id);
  }

  return Array.from(ids);
}

export async function getExecutionById(
  executionId: string,
): Promise<PortalExecution | null> {
  if (!isSupabaseConfigured) return null;
  const { data, error } = await supabase
    .from("installer_opportunities")
    .select(OPPORTUNITY_EXECUTION_COLUMNS)
    .eq("id", executionId)
    .maybeSingle();
  if (error || !data) return null;
  return mapOpportunityToPortalExecution(data as unknown as PortalOpportunityExecutionRow);
}

export async function listExecutionEvents(
  executionId: string,
): Promise<ExecutionEvent[]> {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from("installer_execution_events")
    .select(EVENT_COLUMNS)
    .eq("execution_id", executionId)
    .order("created_at", { ascending: true });
  if (error || !data) return [];
  return data as unknown as ExecutionEvent[];
}

export async function listExecutionProofs(
  executionId: string,
): Promise<ExecutionProof[]> {
  if (!isSupabaseConfigured) return [];
  const { data: checklistProofs } = await supabase
    .from("execution_proofs")
    .select(PROOF_COLUMNS)
    .eq("execution_id", executionId)
    .is("superseded_by", null)
    .order("uploaded_at", { ascending: false });

  const byId = new Map<string, ExecutionProof>();
  for (const proof of (checklistProofs ?? []) as unknown as ExecutionProof[]) {
    byId.set(proof.id, proof);
  }

  return Array.from(byId.values()).sort((a, b) =>
    b.uploaded_at.localeCompare(a.uploaded_at),
  );
}

export async function listExecutionProofsByExecutionIds(
  executionIds: string[],
): Promise<ExecutionProof[]> {
  if (!isSupabaseConfigured) return [];
  const normalizedIds = Array.from(
    new Set(
      executionIds
        .map((id) => String(id ?? "").trim())
        .filter(Boolean),
    ),
  );
  if (normalizedIds.length === 0) return [];

  const { data: checklistProofs } = await supabase
    .from("execution_proofs")
    .select(PROOF_COLUMNS)
    .in("execution_id", normalizedIds)
    .is("superseded_by", null)
    .order("uploaded_at", { ascending: false });

  const byId = new Map<string, ExecutionProof>();
  for (const proof of (checklistProofs ?? []) as unknown as ExecutionProof[]) {
    byId.set(proof.id, proof);
  }

  return Array.from(byId.values()).sort((a, b) =>
    b.uploaded_at.localeCompare(a.uploaded_at),
  );
}

export async function listExecutionProofsByAppJobId(
  appJobId: string,
): Promise<ExecutionProof[]> {
  const executionIds = await listExecutionProofLookupIdsByAppJobId(appJobId);
  return listExecutionProofsByExecutionIds(executionIds);
}

// ── Mutations ──────────────────────────────────────────────
export type AcknowledgeExecutionResult =
  | Ok<{ executionId: string; status: ExecutionStatus; noop?: boolean }>
  | Err;

export async function acknowledgeExecution(
  executionId: string,
): Promise<AcknowledgeExecutionResult> {
  if (!isSupabaseConfigured) return { ok: false, error: "not_configured" };
  try {
    const { data, error } = await supabase.functions.invoke<
      | { ok: true; executionId: string; status: ExecutionStatus; noop?: boolean }
      | { error: string }
    >("execution-acknowledge", { body: { executionId } });
    if (error) return classify(error as { context?: { status?: number }; message: string });
    if (data && "ok" in data && data.ok) return data;
    return { ok: false, error: "unknown" };
  } catch (e) {
    return { ok: false, error: "unknown", detail: String(e) };
  }
}

export interface UpdateExecutionStatusInput {
  executionId: string;
  action: ExecutionAction;
  note?: string;
  summary?: string;
  reason?: string;
}
export type UpdateExecutionStatusResult =
  | Ok<{ executionId: string; status: ExecutionStatus; action: ExecutionAction }>
  | Err;

type UpdateExecutionStatusFunctionResponse =
  | { ok: true; executionId: string; status: ExecutionStatus; action: ExecutionAction }
  | { ok: true; execution: { id?: string | null; status?: string | null } }
  | { ok: true }
  | { error: string };

export async function updateExecutionStatus(
  input: UpdateExecutionStatusInput,
): Promise<UpdateExecutionStatusResult> {
  if (!isSupabaseConfigured) return { ok: false, error: "not_configured" };
  try {
    const { data, error } = await supabase.functions.invoke<
      UpdateExecutionStatusFunctionResponse
    >("execution-status-update", { body: input });
    if (error) return classify(error as { context?: { status?: number }; message: string });
    if (data && "ok" in data && data.ok) {
      if ("executionId" in data) return data;
      if ("execution" in data) {
        return {
          ok: true,
          executionId: data.execution.id ?? input.executionId,
          status: normalizeExecutionStatus(data.execution.status),
          action: input.action,
        };
      }
      return {
        ok: true,
        executionId: input.executionId,
        status: "assigned",
        action: input.action,
      };
    }
    return { ok: false, error: "unknown" };
  } catch (e) {
    return { ok: false, error: "unknown", detail: String(e) };
  }
}

export interface RequestProofUploadInput {
  executionId: string;
  fileName: string;
  kind?: ExecutionProofKind;
  mimeType?: string;
  byteSize?: number;
  caption?: string;
  capturedAt?: string;
  supersedesId?: string;
}
export type RequestProofUploadResult =
  | Ok<{ proofId: string; uploadUrl: string; token: string; path: string }>
  | Err;

export async function requestExecutionProofUploadUrl(
  input: RequestProofUploadInput,
): Promise<RequestProofUploadResult> {
  if (!isSupabaseConfigured) return { ok: false, error: "not_configured" };
  try {
    const { data, error } = await supabase.functions.invoke<
      | { ok: true; proofId: string; uploadUrl: string; token: string; path: string }
      | { error: string }
    >("execution-proof-upload-url", { body: input });
    if (error) return classify(error as { context?: { status?: number }; message: string });
    if (data && "ok" in data && data.ok) return data;
    return { ok: false, error: "unknown" };
  } catch (e) {
    return { ok: false, error: "unknown", detail: String(e) };
  }
}

export async function uploadProofBytes(
  uploadUrl: string,
  token: string,
  mimeType: string,
  file: Blob,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": mimeType,
      "x-upsert": "false",
    },
    body: file,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Proof upload failed (${res.status}): ${text || res.statusText}`);
  }
}

export type RequestProofReadResult =
  | Ok<{
      proofId: string; readUrl: string;
      fileName: string | null; mimeType: string | null;
      expiresInSeconds: number;
    }>
  | Err;

export async function requestExecutionProofReadUrl(
  proofId: string,
  expiresInSeconds?: number,
): Promise<RequestProofReadResult> {
  if (!isSupabaseConfigured) return { ok: false, error: "not_configured" };
  const directReadUrl = async (): Promise<RequestProofReadResult | null> => {
    const { data: proof, error: proofError } = await supabase
      .from("execution_proofs")
      .select("id, file_name, mime_type, storage_path")
      .eq("id", proofId)
      .maybeSingle();
    if (proofError) return null;
    const storagePath = String((proof as { storage_path?: string | null } | null)?.storage_path ?? "").trim();
    if (!storagePath) return null;
    const { data, error } = await supabase.storage
      .from("execution-proofs")
      .createSignedUrl(storagePath, expiresInSeconds ?? 300);
    if (error || !data?.signedUrl) return null;
    return {
      ok: true,
      proofId,
      readUrl: data.signedUrl,
      fileName: String((proof as { file_name?: string | null } | null)?.file_name ?? "").trim() || null,
      mimeType: String((proof as { mime_type?: string | null } | null)?.mime_type ?? "").trim() || null,
      expiresInSeconds: expiresInSeconds ?? 300,
    };
  };

  try {
    // Koredor App reviewers/admins are not contractor crew members, so prefer
    // the app/org RLS path before calling the contractor-portal edge function.
    const direct = await directReadUrl();
    if (direct) return direct;

    const { data, error } = await supabase.functions.invoke<
      | {
          ok: true; proofId: string; readUrl: string;
          fileName: string | null; mimeType: string | null;
          expiresInSeconds: number;
        }
      | { url: string; expiresAt?: string; fileName?: string | null; mimeType?: string | null }
      | { error: string; detail?: string; code?: string }
    >("execution-proof-read-url", { body: { proofId, expiresInSeconds } });
    if (error) {
      return classify(error as { context?: { status?: number }; message: string });
    }
    if (data && "ok" in data && data.ok) return data;
    if (data && "url" in data && typeof data.url === "string") {
      return {
        ok: true,
        proofId,
        readUrl: data.url,
        fileName: "fileName" in data && typeof data.fileName === "string" ? data.fileName : null,
        mimeType: "mimeType" in data && typeof data.mimeType === "string" ? data.mimeType : null,
        expiresInSeconds: expiresInSeconds ?? 300,
      };
    }
    if (data && "error" in data && typeof data.error === "string") {
      const detail = "detail" in data && typeof data.detail === "string" ? data.detail : data.error;
      return {
        ok: false,
        error: data.error.toLowerCase() === "forbidden" ? "forbidden" : "unknown",
        detail,
      };
    }
    return { ok: false, error: "unknown" };
  } catch (e) {
    const direct = await directReadUrl();
    if (direct) return direct;
    return { ok: false, error: "unknown", detail: String(e) };
  }
}
